/** Parked-only Adventure scooter using the team's internal SVL Activa geometry.
 * Pose/obstacle ownership remains with the caller; no collider or sim state is added.
 * Source and extraction hashes: benchmark/parked-scooter.manifest.json.
 */
import type { Scene } from '@babylonjs/core/scene';
import { Mesh } from '@babylonjs/core/Meshes/mesh';
import { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import { LoadAssetContainerAsync } from '@babylonjs/core/Loading/sceneLoader';
import type { AssetContainer } from '@babylonjs/core/assetContainer';
import { PBRMaterial } from '@babylonjs/core/Materials/PBR/pbrMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Vector3 } from '@babylonjs/core/Maths/math.vector';
import { buildAdventureScooter, type AdventureActor } from './adventureCast';
import { themeAssetPath } from './themes';
import '@babylonjs/loaders/glTF';

type LoadState = { pending: Set<string>; failed: Set<string> };
export interface AdventureParkedScooterOptions {
  /** Existing obstacle dimensions, in the actor's local X / Z frame. */
  width: number;
  length: number;
  loadState?: LoadState;
  onReady?: (meshes: Mesh[]) => void;
}
function bounds(meshes: readonly Mesh[]): { min: Vector3; max: Vector3 } {
  let min = new Vector3(Infinity, Infinity, Infinity), max = min.negate();
  for (const mesh of meshes) {
    mesh.computeWorldMatrix(true);
    const box = mesh.getBoundingInfo().boundingBox;
    min = Vector3.Minimize(min, box.minimumWorld);
    max = Vector3.Maximize(max, box.maximumWorld);
  }
  return { min, max };
}

/** Return a stable root with the original visible scooter as loading/error fallback.
 * The adapter already fits width/length; the caller only sets parent/position/yaw.
 */
export function buildAdventureParkedScooter(scene: Scene, options: AdventureParkedScooterOptions): AdventureActor {
  if (!Number.isFinite(options.width) || !Number.isFinite(options.length) || options.width <= 0 || options.length <= 0) {
    throw new Error('Adventure parked scooter requires positive obstacle dimensions');
  }
  const path = themeAssetPath('neighborhood', 'models', 'benchmark/parked-scooter');
  if (!path) throw new Error('Missing Adventure benchmark parked-scooter asset');
  const url = `${import.meta.env.BASE_URL}${path}`;
  const root = new TransformNode('adventureScooter', scene);
  let fallback: AdventureActor | undefined = buildAdventureScooter(scene, false);
  fallback.root.name = 'adventureParkedScooterFallback';
  const fallbackBounds = bounds(fallback.meshes), fallbackSize = fallbackBounds.max.subtract(fallbackBounds.min);
  // Match the original prop's fitting: horizontal dimensions fit the obstacle,
  // while the fallback's authored height remains unchanged.
  fallback.root.scaling.set(options.width / fallbackSize.x, 1, options.length / fallbackSize.z);
  fallback.root.position.set(-(fallbackBounds.min.x + fallbackBounds.max.x) * .5 * fallback.root.scaling.x,
    -fallbackBounds.min.y, -(fallbackBounds.min.z + fallbackBounds.max.z) * .5 * fallback.root.scaling.z);
  fallback.root.parent = root;
  const meshes: Mesh[] = [...fallback.meshes];
  let disposed = false, container: AssetContainer | undefined, fit: TransformNode | undefined;
  const ownedMaterials: PBRMaterial[] = [];
  const disposeLoaded = (): void => {
    for (const material of ownedMaterials.splice(0)) material.dispose(false, false);
    container?.dispose(); container = undefined;
    fit?.dispose(); fit = undefined;
  };
  options.loadState?.pending.add(url);
  void LoadAssetContainerAsync(url, scene).then(loaded => {
    if (disposed || scene.isDisposed || root.isDisposed()) { loaded.dispose(); return; }
    container = loaded;
    fit = new TransformNode('adventureParkedScooter_fit', scene);
    loaded.addAllToScene();
    for (const node of loaded.rootNodes) if (node instanceof TransformNode) node.parent = fit;
    const nextMeshes = loaded.meshes.filter((mesh): mesh is Mesh => mesh instanceof Mesh && mesh.getTotalVertices() > 0);
    if (nextMeshes.length !== 4) throw new Error('Adventure scooter asset contains unexpected geometry');
    const source = bounds(nextMeshes), centerZ = (source.min.z + source.max.z) / 2;
    // The handlebar/mirrors are the high vertices in the textured body mesh.
    // Inspect them after the glTF loader's handedness conversion so +Z remains forward.
    const body = nextMeshes.find(mesh => mesh.material?.name === 'HondaActiva' && mesh.getTotalVertices() > 1000);
    const vertices = body?.getVerticesData('position');
    if (!body || !vertices) throw new Error('Adventure scooter front marker is missing');
    const bodyMatrix = body.computeWorldMatrix(true);
    let frontSum = 0, frontCount = 0;
    for (let i = 0; i < vertices.length; i += 3) {
      const point = Vector3.TransformCoordinates(new Vector3(vertices[i]!, vertices[i + 1]!, vertices[i + 2]!), bodyMatrix);
      if (point.y > source.min.y + (source.max.y - source.min.y) * .86) { frontSum += point.z; frontCount++; }
    }
    if (!frontCount) throw new Error('Adventure scooter has no handlebar marker');
    fit.rotation.y = frontSum / frontCount < centerZ ? Math.PI : 0;
    const oriented = bounds(nextMeshes), size = oriented.max.subtract(oriented.min);
    if (size.x <= 0 || size.z <= 0) throw new Error('Adventure scooter has invalid bounds');
    const longitudinalScale = options.length / size.z;
    fit.scaling.set(options.width / size.x, longitudinalScale, longitudinalScale);
    fit.position.set(-(oriented.min.x + oriented.max.x) * .5 * fit.scaling.x,
      -oriented.min.y * fit.scaling.y, -(oriented.min.z + oriented.max.z) * .5 * fit.scaling.z);
    fit.parent = root;
    for (const material of loaded.materials) {
      if (!(material instanceof PBRMaterial)) continue;
      material.environmentIntensity = .9; material.emissiveColor.setAll(0);
      if (material.name === 'Body') {
        material.albedoColor = Color3.FromHexString('#973B24').toLinearSpace();
        material.metallic = .12; material.roughness = .3;
        material.clearCoat.isEnabled = true; material.clearCoat.intensity = .45;
        material.clearCoat.roughness = .22;
      } else { material.metallic = 0; material.roughness = .62; }
    }
    const atlas = loaded.materials.find(material => material.name === 'HondaActiva');
    const tireMaterial = atlas instanceof PBRMaterial ? new PBRMaterial('adventureParkedScooter_rubber', scene) : null;
    if (tireMaterial && atlas instanceof PBRMaterial) {
      // Share the container-owned atlas explicitly; PBRMaterial.clone would clone its texture.
      tireMaterial.albedoTexture = atlas.albedoTexture; tireMaterial.albedoColor.copyFrom(atlas.albedoColor);
      tireMaterial.metallic = 0; tireMaterial.roughness = .88; tireMaterial.environmentIntensity = .65;
      ownedMaterials.push(tireMaterial);
    }
    for (const mesh of nextMeshes) {
      const localSize = mesh.getBoundingInfo().boundingBox.extendSize.scale(2);
      if (tireMaterial && localSize.y < .65) mesh.material = tireMaterial;
      mesh.name = `adventureParkedScooter_${mesh.name}`;
      mesh.isPickable = false; mesh.receiveShadows = true;
    }
    for (const animation of loaded.animationGroups) animation.stop();
    // Keep fallback ownership until all registration work succeeds.
    options.onReady?.(nextMeshes);
    fallback?.dispose(); fallback = undefined;
    meshes.splice(0, meshes.length, ...nextMeshes);
    options.loadState?.pending.delete(url);
    options.loadState?.failed.delete(url);
  }).catch(error => {
    if (disposed) return;
    disposeLoaded();
    options.loadState?.pending.delete(url);
    options.loadState?.failed.add(url);
    console.error('Adventure parked scooter failed to load', error);
  });
  return {
    root, meshes,
    update(timeS, speedMps) { fallback?.update(timeS, speedMps); },
    dispose() {
      if (disposed) return;
      disposed = true;
      options.loadState?.pending.delete(url);
      fallback?.dispose(); fallback = undefined;
      disposeLoaded(); root.dispose(); meshes.length = 0;
    },
  };
}
